/**
 * Which detached windows are currently open (#761 review).
 *
 * Needed by work that must run in a *visible* window rather than in the window
 * that happens to own a given element. The playback clock is the case that forced
 * this: it lives in the main window, but while the user watches a detached viewer
 * the main window is in the background, where the browser throttles
 * `requestAnimationFrame` to nothing and timers to ~1 Hz — which is why the EEG
 * cursor advanced in one-second steps instead of sweeping smoothly.
 *
 * `usePopoutWindow` keeps this registry up to date; nothing else should mutate it.
 */

const open = new Set<Window>();

export function registerPopoutWindow(win: Window): void {
    open.add(win);
}

export function unregisterPopoutWindow(win: Window): void {
    open.delete(win);
}

/** Every detached window still open, in registration order. */
export function popoutWindows(): Window[] {
    return [...open].filter(w => !w.closed);
}

/**
 * A window the user can actually see, preferring the main one.
 *
 * Falls back to the main window when everything reports hidden — better a
 * throttled clock than none, and the caller cannot do anything smarter.
 */
export function visibleWindow(): Window {
    if (typeof document !== 'undefined' && !document.hidden) return window;
    for (const win of open) {
        try {
            if (!win.closed && !win.document.hidden) return win;
        } catch {
            // Window torn down mid-check; skip it.
        }
    }
    return window;
}
